import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  assetRequestTiming,
  isAssetRequestOpen,
  tidyText,
  today,
  type AssetRequestReviewOutcome,
} from '@cfl/assets-shared';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { TasksArt } from '@/components/illustrations';
import { PageContainer } from '@/components/shell/page-container';
import { PILL_CLASS } from '@/lib/badges';
import { ApiError } from '@/lib/api';
import {
  useAssetRequestQueue,
  useReviewAssetRequest,
  type AssetRequest,
} from '@/lib/asset-requests-api';
import { useCan } from '@/lib/session';
import { RequestRow } from '@/routes/request-asset';
import { cn } from '@/lib/utils';

/**
 * IT's queue of what people have asked for.
 *
 * Its own screen rather than a card on the register, unlike the repair review queue which
 * lives on the Repairs page. A repair request and the repair it becomes are the same work
 * seen at two moments, so they belong together. An asset request is the opposite end of
 * the lifecycle - it is about hardware that does not exist here yet, and it is answered
 * by going to Assign, not by editing anything on this page.
 *
 * Sorted by how soon each is needed rather than when it was raised: a request for a
 * joiner starting Monday outranks one raised a fortnight ago for no particular date.
 */
export function RequestsPage() {
  const canReview = useCan('manageAssets');
  const { data: requests, isPending } = useAssetRequestQueue();
  const [reviewing, setReviewing] = React.useState<AssetRequest | null>(null);

  const open = requests?.filter((r) => isAssetRequestOpen(r.status)) ?? [];
  const closed = requests?.filter((r) => !isAssetRequestOpen(r.status)) ?? [];
  const late = open.filter((r) => assetRequestTiming(r.neededBy, today()) === 'LATE').length;

  return (
    <PageContainer>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">
            Waiting on IT
            {open.length > 0 && (
              <Badge
                variant={late > 0 ? 'soft-destructive' : 'soft-warning'}
                className={`${PILL_CLASS} ml-2`}
              >
                {late > 0 ? `${late} past its date` : `${open.length} open`}
              </Badge>
            )}
          </CardTitle>
          <CardDescription className="text-xs">
            Hardware people have asked for. Nothing here is reserved — the register is unchanged
            until you issue something against a request on the Assign screen, which is also what
            closes it.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {isPending ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : open.length === 0 ? (
            <EmptyState
              art={TasksArt}
              title="Nothing outstanding"
              body={
                <>
                  Anyone can ask for hardware from their own
                  <Link to="/my-assets" className="mx-1 underline underline-offset-2">
                    My assets
                  </Link>
                  screen.
                </>
              }
            />
          ) : (
            <ul className="divide-y">
              {open.map((request) => (
                <RequestRow
                  key={request.id}
                  request={request}
                  onReview={canReview ? () => setReviewing(request) : undefined}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {closed.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Already dealt with</CardTitle>
            <CardDescription className="text-xs">
              Issued, declined or withdrawn. Kept so &ldquo;what happened about my laptop&rdquo;
              has an answer.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="divide-y">
              {closed.map((request) => (
                <RequestRow key={request.id} request={request} />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <ReviewDialog request={reviewing} onClose={() => setReviewing(null)} />
    </PageContainer>
  );
}

/**
 * The two outcomes IT can set by hand.
 *
 * There is deliberately no "Fulfilled" here. Issuing an asset against the request is what
 * marks it fulfilled, and it happens on the Assign screen - so the status can never exist
 * without a real handover behind it. A button here would be the one way to empty this
 * queue without anybody getting anything, and the database refuses it too (see
 * `asset_request_assignment_matches_status`).
 */
const OUTCOMES: Array<{ status: AssetRequestReviewOutcome; title: string; blurb: string }> = [
  {
    status: 'ACKNOWLEDGED',
    title: 'Seen it, sourcing something',
    blurb:
      'Tells the requester it has not been ignored. Keeps it on this queue and keeps the reminders counting down.',
  },
  {
    status: 'DECLINED',
    title: 'Not issuing anything',
    blurb:
      'Say why — they read this. "No" with no reason is how people go back to messaging IT directly.',
  },
];

function ReviewDialog({
  request,
  onClose,
}: {
  request: AssetRequest | null;
  onClose: () => void;
}) {
  const review = useReviewAssetRequest(request?.id ?? '');
  const [status, setStatus] = React.useState<AssetRequestReviewOutcome | null>(null);
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (request) {
      setStatus(null);
      setNote('');
    }
  }, [request]);

  const submit = async () => {
    if (!status) return;
    try {
      await review.mutateAsync({ status, note: note.trim() || null });
      toast.success(
        status === 'DECLINED'
          ? 'Declined. The requester can see your reason.'
          : 'Marked as seen. The requester knows it is being looked at.',
      );
      onClose();
    } catch (error) {
      const message =
        error instanceof ApiError
          ? (Object.values(error.fieldErrors ?? {})[0]?.[0] ?? error.message)
          : 'That could not be saved.';
      toast.error(message);
    }
  };

  return (
    <Dialog open={request !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review this request</DialogTitle>
          <DialogDescription>
            {request?.category.name} for {request?.forEmployee.name} — asked by{' '}
            {request?.requestedBy.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="whitespace-pre-wrap rounded-md bg-muted/60 p-3 text-xs">
            {request ? tidyText(request.justification) : null}
          </p>

          <div className="space-y-2">
            {OUTCOMES.map((outcome) => (
              <button
                key={outcome.status}
                type="button"
                onClick={() => setStatus(outcome.status)}
                className={cn(
                  'w-full rounded-md border p-3 text-left transition-colors',
                  status === outcome.status ? 'border-primary bg-accent' : 'hover:bg-muted/50',
                )}
              >
                <div className="text-sm font-medium">{outcome.title}</div>
                <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                  {outcome.blurb}
                </div>
              </button>
            ))}
          </div>

          {/* Said outright, because "where is the Fulfilled button" is the first
              question anyone will have on this dialog. The link carries both ids so the
              Assign form arrives with the person and the request already chosen. */}
          <div className="rounded-md bg-muted/40 p-3">
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              To settle this by actually issuing something, do it on Assign — that records the
              handover and closes the request together, so a closed request always has a real
              asset behind it.
            </p>
            <Button hint={"Assign an asset to fulfil this request"} asChild size="sm" variant="secondary" className="mt-2 h-7">
              <Link
                to={`/assign?employee=${request?.forEmployee.id ?? ''}&request=${request?.id ?? ''}`}
              >
                Assign something against this
              </Link>
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label>
              Note to whoever asked{status === 'DECLINED' ? '' : ' (optional)'}
            </Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                status === 'DECLINED'
                  ? 'Why nothing is being issued — they will read this.'
                  : 'When you expect to have one, what you are ordering.'
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!status || review.isPending || (status === 'DECLINED' && !note.trim())}
            onClick={() => void submit()}
          >
            {review.isPending ? 'Saving…' : 'Save'}
          </Button>
          {(!status || (status === 'DECLINED' && !note.trim())) && <span className="self-center text-xs text-muted-foreground">{!status ? 'Choose a decision first' : 'Add a note saying why it is declined'}</span>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
